import type { Node } from '@xyflow/react';
import { getAvailableVariables } from '@/components/automation/variableUtils';

/**
 * Pure helpers for the Shopify Playbooks "Enable" dialog.
 *
 * - `detectTemplatePlaceholders(components)` walks every text-bearing
 *   Meta template component and returns the unique `{{N}}` / `{{name}}`
 *   placeholders, in the order the recipient will see them.
 *
 * - `getVariableSuggestions(triggerType)` returns the chip list we show
 *   in the "Variable library" panel. It delegates to
 *   [variableUtils.getAvailableVariables](web/src/components/automation/variableUtils.ts)
 *   — the SAME source the WhatsApp flow-builder's variable picker uses —
 *   by feeding it a synthetic one-node graph that mimics the playbook's
 *   generated flow. This guarantees the chips stay in lockstep with the
 *   flow builder: when the builder gains a new Shopify field (e.g. a new
 *   trigger type), the playbook dialog picks it up automatically.
 *
 * Kept free of React so they can be unit-tested without jsdom.
 */

export interface TemplatePlaceholder {
  /** The raw key inside `{{ }}` — e.g. "1" or "first_name". */
  key: string;
  /** Where in the template the placeholder appears — used for the field label. */
  source: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTON';
  /** Display label — "Body — Variable {{1}}" etc. */
  label: string;
}

const SOURCE_ORDER: TemplatePlaceholder['source'][] = ['HEADER', 'BODY', 'FOOTER', 'BUTTON'];

function collectTextsFromComponents(
  components: any[]
): Array<{ text: string; source: TemplatePlaceholder['source'] }> {
  if (!Array.isArray(components)) return [];
  const buckets: Record<TemplatePlaceholder['source'], string[]> = {
    HEADER: [],
    BODY: [],
    FOOTER: [],
    BUTTON: [],
  };

  for (const c of components) {
    const type = String(c?.type || '').toUpperCase();
    if (type === 'HEADER' && typeof c?.text === 'string') buckets.HEADER.push(c.text);
    if (type === 'BODY' && typeof c?.text === 'string') buckets.BODY.push(c.text);
    if (type === 'FOOTER' && typeof c?.text === 'string') buckets.FOOTER.push(c.text);
    if (type === 'BUTTONS' && Array.isArray(c?.buttons)) {
      for (const b of c.buttons) {
        const btnType = String(b?.type || '').toUpperCase();
        if (btnType === 'URL' && typeof b?.url === 'string') buckets.BUTTON.push(b.url);
      }
    }
  }

  const out: Array<{ text: string; source: TemplatePlaceholder['source'] }> = [];
  for (const source of SOURCE_ORDER) {
    for (const text of buckets[source]) out.push({ text, source });
  }
  return out;
}

/**
 * Extract every unique placeholder across the template, preserving the
 * recipient's reading order (HEADER → BODY → FOOTER → BUTTON) regardless
 * of the input array order.
 */
export function detectTemplatePlaceholders(components: any[]): TemplatePlaceholder[] {
  const texts = collectTextsFromComponents(components);
  const seen = new Set<string>();
  const out: TemplatePlaceholder[] = [];

  const pattern = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  for (const { text, source } of texts) {
    let m: RegExpExecArray | null;
    pattern.lastIndex = 0;
    while ((m = pattern.exec(text)) !== null) {
      const key = m[1];
      if (seen.has(key)) continue;
      seen.add(key);
      const sourceLabel = source.charAt(0) + source.slice(1).toLowerCase();
      out.push({
        key,
        source,
        label: `${sourceLabel} — Variable {{${key}}}`,
      });
    }
  }
  return out;
}

/**
 * Render what the recipient will see with the supplied variable map applied.
 * Unsupplied variables are left as `{{key}}` so users know they're still pending.
 */
export function renderTemplatePreview(bodyText: string, variables: Record<string, string>): string {
  if (!bodyText) return '';
  return bodyText.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
    const raw = variables?.[key];
    if (raw == null || raw === '') return match;
    return raw;
  });
}

// ── Suggestion catalog — delegates to variableUtils ──────────────────────

export interface VariableSuggestion {
  /** Full `{{expression}}` to insert into the variable input. */
  value: string;
  /** User-facing label for the chip. */
  label: string;
  /** Group the chip belongs to — drives the section header in the panel. */
  category: 'contact' | 'system' | 'trigger';
}

/**
 * Map playbook `triggerType` (e.g. `"order.paid"`) to the flow-builder's
 * concrete node type (`"shopify_order_paid_trigger"`). Must stay in sync
 * with the catalog at
 * [backend/src/domain/constants/shopifyPlaybooks.js](backend/src/domain/constants/shopifyPlaybooks.js)
 * — regression-locked in `shopifyPlaybookPlaceholders.test.ts`.
 *
 * We duplicate this here (tiny, static) instead of round-tripping through
 * the backend `/catalog` endpoint just to resolve a string. If the trigger
 * is unknown we fall back to the generic `shopify_trigger` multi-handle node.
 */
const TRIGGER_TO_NODE_TYPE: Record<string, string> = {
  'order.paid': 'shopify_order_paid_trigger',
  'order.placed': 'shopify_order_created_trigger',
  'order.cancelled': 'shopify_order_cancelled_trigger',
  'order.updated': 'shopify_order_updated_trigger',
  'order.shipped': 'shopify_order_shipped_trigger',
  'order.out_for_delivery': 'shopify_order_out_for_delivery_trigger',
  'order.delivered': 'shopify_order_delivered_trigger',
  'checkout.updated': 'shopify_abandoned_cart_trigger',
  'customer.created': 'shopify_customer_created_trigger',
  'refund.created': 'shopify_refund_created_trigger',
};

/** Node id used inside the synthetic graph fed to `getAvailableVariables`.
 * Must match the trigger node id used when we actually generate the flow
 * in [ShopifyPlaybookService.buildPlaybookFlowGraph](backend/src/application/services/ShopifyPlaybookService.js)
 * — keeping them identical means the `{{playbook-trigger.orderDetails.name}}`
 * expression a user inserts from a chip resolves at runtime against the
 * real trigger node in the persisted flow graph. */
const PLAYBOOK_TRIGGER_NODE_ID = 'playbook-trigger';

/**
 * Strings from the flow-builder catalog we don't want to expose in a
 * playbook context. API credentials would leak into the customer's
 * message; `message`/`phoneNumber` are flow-runtime-only.
 */
const EXCLUDED_VAR_PATHS = new Set([
  PLAYBOOK_TRIGGER_NODE_ID, // bare "(Formatted)" entry — just the whole payload
  'message',
  'phoneNumber',
  'shopify.access_token',
  'shopify.shop',
  'shopify.api_version',
  'petpooja.app_key',
  'petpooja.app_secret',
  'petpooja.access_token',
  'petpooja.base_url',
  'petpooja.restaurant_id',
]);

/**
 * Strip the node's label prefix from trigger-specific variable labels.
 * `getAvailableVariables` emits labels like `"Trigger Order Name"` because
 * the node data.label is `"Trigger"`. For the playbook dialog the prefix
 * is redundant (the user already knows the chips are trigger-scoped),
 * so we drop it.
 */
function cleanLabel(label: string, prefix: string): string {
  if (label.startsWith(prefix + ' ')) return label.substring(prefix.length + 1);
  return label;
}

/**
 * Return the full suggestion list for a trigger type. Pulls from the
 * same `getAvailableVariables` function the WhatsApp flow-builder uses,
 * so the chips here match what users see when they click "Customize".
 */
export function getVariableSuggestions(triggerType: string): VariableSuggestion[] {
  const triggerNodeType = TRIGGER_TO_NODE_TYPE[triggerType] || 'shopify_trigger';
  const label = 'Trigger';

  // Synthesise a minimal flow that mirrors our playbook-generated graph:
  // one trigger node of the right type. `getAvailableVariables` reads
  // `n.id`, `n.type`, and `n.data.label` to emit variable paths.
  const syntheticNodes: Node[] = [
    {
      id: PLAYBOOK_TRIGGER_NODE_ID,
      type: triggerNodeType,
      position: { x: 0, y: 0 },
      data: { label },
    } as unknown as Node,
  ];

  const vars = getAvailableVariables(syntheticNodes);

  const suggestions: VariableSuggestion[] = [];
  for (const v of vars) {
    if (EXCLUDED_VAR_PATHS.has(v.id)) continue;

    // Classify so the panel can group chips by section.
    let category: VariableSuggestion['category'];
    if (v.category === 'contact') category = 'contact';
    else if (v.id.startsWith(PLAYBOOK_TRIGGER_NODE_ID + '.')) category = 'trigger';
    else category = 'system';

    suggestions.push({
      value: `{{${v.id}}}`,
      label: category === 'trigger' ? cleanLabel(v.label, label) : v.label,
      category,
    });
  }

  return suggestions;
}

/**
 * Convenience: group suggestions into the three sections the UI renders.
 * Keeps the dialog free of grouping logic.
 */
export function groupSuggestions(
  suggestions: VariableSuggestion[]
): Array<{ title: string; items: VariableSuggestion[]; category: VariableSuggestion['category'] }> {
  const buckets: Record<VariableSuggestion['category'], VariableSuggestion[]> = {
    trigger: [],
    contact: [],
    system: [],
  };
  for (const s of suggestions) buckets[s.category].push(s);
  return [
    { title: 'From this event', items: buckets.trigger, category: 'trigger' as const },
    { title: 'Contact fields', items: buckets.contact, category: 'contact' as const },
    { title: 'System', items: buckets.system, category: 'system' as const },
  ].filter((g) => g.items.length > 0);
}

/**
 * Filter suggestions by a free-text query. Case-insensitive; matches against
 * either the human label ("Order Name") or the raw expression
 * ("{{playbook-trigger.orderDetails.name}}"). Empty/whitespace queries
 * return the input unchanged so callers can always feed the result back
 * into `groupSuggestions`.
 */
export function filterSuggestions(
  suggestions: VariableSuggestion[],
  query: string
): VariableSuggestion[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) return suggestions;
  return suggestions.filter(
    (s) => s.label.toLowerCase().includes(q) || s.value.toLowerCase().includes(q)
  );
}
